import { useId, useRef, useState } from 'react';
import { type AssetKind, quizApi } from '../../lib/quizApi.js';

interface Props {
  quizId: string;
  /** Names the object key only — see quizApi.uploadAsset. */
  kind: Extract<AssetKind, 'question' | 'option'>;
  imageUrl: string | undefined;
  disabled: boolean;
  /** What a screen reader hears instead of "Image": there are many on a page. */
  label: string;
  onChange: (imageUrl: string | undefined) => void;
}

/**
 * Story #75 — one content image, uploaded the way `ThemeEditor` uploads a logo.
 *
 * Cleared to `undefined` rather than to an empty string: `imageUrl` is optional
 * and absent is what "no image" is spelled as everywhere else — the validator,
 * the projections, the snapshot. An empty string would be a second spelling of
 * it that `parseScreens` would drop on the next save anyway, so the builder
 * would disagree with what it just saved until the page was reloaded.
 */
export function ImageField({ quizId, kind, imageUrl, disabled, label, onChange }: Props) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const inputId = useId();

  async function upload(file: File | undefined) {
    if (!file) return;
    setError(null);
    setUploading(true);

    try {
      onChange(await quizApi.uploadAsset(quizId, kind, file));
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : 'Upload failed. Try a PNG, JPEG or WebP under 5 MB.',
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="image-field">
      {imageUrl ? (
        // Empty alt: the picture is decoration on a control that is already
        // labelled, and describing it would read the URL aloud.
        <img className="image-field-preview" src={imageUrl} alt="" />
      ) : null}

      <label htmlFor={inputId} className="image-field-label">
        {label}
      </label>
      <input
        id={inputId}
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        disabled={disabled || uploading}
        onChange={(e) => void upload(e.target.files?.[0])}
      />

      {imageUrl ? (
        <button
          type="button"
          className="secondary field-clear"
          disabled={disabled}
          onClick={() => {
            onChange(undefined);
            // Otherwise re-picking the same file fires no change event.
            if (input.current) input.current.value = '';
          }}
        >
          Remove image
        </button>
      ) : null}

      {uploading ? <p className="muted">Uploading…</p> : null}
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
